import React, { useEffect, useMemo, useState } from 'react'
import './Configs.css'
import { useNavigate, useParams } from 'react-router-dom'
import HeaderBar from '../../components/HeaderBar/HeaderBar'
import ConfigReview from './ConfigReview/ConfigReview'
import ComponentBox from '../../components/ComponentBox/ComponentBox'
import { useSelector, useDispatch } from 'react-redux'
import { DefaultState, IPAConfig } from '../../types'
import { Container, Col, Row, ModalBody } from 'reactstrap'
import {
  getConfigs,
  removeConfig,
  setEntityError,
  getConfigFile,
  downloadConfigFile,
} from '../../redux/slices'
import { createEntityAdapter } from '@reduxjs/toolkit'
import ICSActionButton from '../../components/ICS/ActionButton/ICSActionButton'
import ICSErrorCard from '../../components/ICSErrorCard/ICSErrorCard'
import * as Types from '../../types'
import { ICSModal, TanStackDatagridWithExport } from '../../components'
import { ICSDataCellDetailView } from '../../components/ICSDataCellDetailView/ICSDataCellDetailView'
import ConfigSave from './ConfigSave/ConfigSave'
import { AppDispatch } from '../../services/lib'
import {
  TanStackDatagridColumn,
  TanStackDatagridFilter,
  TanStackDatagridPagination,
  TanStackDatagridRowAction,
  TanStackDatagridSort
} from '../../components/TanStackDataGrid/TanStackDatagrid'
import { useTimezone } from '../../hooks/useTimezone'

interface ConfigRow extends IPAConfig {
  user?: {
    name?: string
  }
  created_at?: string
  updated_at?: string
}

const Configs = () => {
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const { formatLocalTimeFromUtc } = useTimezone()
  const isAdmin: boolean | undefined = useSelector(
    (state: DefaultState) => state.session.data.user?.roles?.includes('admin')
  )
  const { scriptId } = useParams() as { scriptId: string }
  const [modalVisible, setModalVisible] = useState<boolean>(false)
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null)
  const [uploadModalVisible, setUploadModalVisible] = useState<boolean>(false)
  const [sort, setSort] = useState<TanStackDatagridSort | null>(null)
  const [filters, setFilters] = useState<TanStackDatagridFilter[]>([])
  const [pagination, setPagination] = useState<TanStackDatagridPagination>({
    pageIndex: 0,
    pageSize: 10
  })
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({})
  const configsState = useSelector((state: DefaultState) => state.configs)
  const configsError = useSelector((state: DefaultState) => state.configs.error)
  const configsArray = createEntityAdapter()
    .getSelectors()
    .selectAll(configsState) as ConfigRow[]

  useEffect(() => {
    if (!scriptId) return navigate('/ui2/scripts')
    const initialData = async () => {
      await dispatch(getConfigs(scriptId))
    }

    initialData()
  }, [dispatch, navigate, scriptId])

  function hideModal() {
    setModalVisible(false)
    setSelectedConfigId(null)
  }

  function hideUploadModal() {
    dispatch(
      setEntityError({
        sliceName: 'configs',
        entityName: 'Config',
        error: null,
      })
    )
    setUploadModalVisible(false)
  }

  const download = async (data: Types.IPAConfig) => {
    const configFileRes = await dispatch(
      getConfigFile({
        scriptId,
        configId: data.id,
      })
    )

    if (configFileRes.type.split('/').pop() === 'fulfilled') {
      const downloadRes = await dispatch(
        downloadConfigFile({
          data: (configFileRes.payload as Types.IPAConfig).file,
          fileName: data.name,
        })
      )

      downloadRes.type.split('/').pop() === 'fulfilled' &&
        dispatch(getConfigs(scriptId))
    }
  }

  const data = useMemo(() => configsArray ?? [], [configsArray])

  const handleView = (config: ConfigRow) => {
    if (!scriptId) return

    setSelectedConfigId(config.id)
    setModalVisible(true)
  }

  const handleDelete = async (config: ConfigRow) => {
    if (!scriptId) return

    const result = await dispatch(
      removeConfig({
        sId: scriptId,
        cId: config.id
      })
    )

    if (removeConfig.fulfilled.match(result)) {
      dispatch(getConfigs(scriptId))
    }
  }

  const columns = useMemo<TanStackDatagridColumn<ConfigRow>[]>(() => [
    {
      id: 'name',
      header: 'Name',
      accessor: (config) => config.name || '',
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'description',
      header: 'Description',
      accessor: (config) => config.description || '',
      cell: (config) => (
        <ICSDataCellDetailView
          value={config.description || ''}
          data={config}
          row={{ data: config }}
          modalWidth='35vw'
          modalHeight='20vh'
        />
      ),
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'userName',
      header: 'User',
      accessor: (config) => config.user?.name || '',
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'created_at',
      header: 'Date Created',
      accessor: (config) => config.created_at || '',
      cell: (config) => formatLocalTimeFromUtc(config.created_at) ?? '',
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    },
    {
      id: 'updated_at',
      header: 'Last Updated',
      accessor: (config) => config.updated_at || '',
      cell: (config) => formatLocalTimeFromUtc(config.updated_at) ?? '',
      sortable: true,
      filterable: true,
      resizable: true,
      hideable: true
    }
  ], [formatLocalTimeFromUtc])

  const actions = useMemo<TanStackDatagridRowAction<ConfigRow>[]>(() => {
    const nextActions: TanStackDatagridRowAction<ConfigRow>[] = [
      {
        id: 'view',
        icon: 'lucide#view',
        label: 'View',
        onClick: handleView
      },
      {
        id: 'download',
        icon: 'lucide#arrow-down',
        label: 'Download',
        onClick: download
      }
    ]

    if (isAdmin) {
      nextActions.splice(1, 0, {
        id: 'delete',
        icon: 'lucide#trash',
        label: 'Delete',
        onClick: handleDelete
      })
    }

    return nextActions
  }, [isAdmin, scriptId])

  return (
    <>
      <Container fluid={true}>
        <Row>
          <Col>
            <HeaderBar
              origin='IPA'
              routes={[
                {
                  title: 'Scripts',
                  url: '/ui2/scripts/',
                },
                {
                  title: 'Configurations',
                },
              ]}
              help={{
                title: 'Script Configurations',
                text: `Configurations are optional for Analyzers but can be used to configure the operation of an Analysis Script based on parameters contained within. It could be as simple as passing a parameter to make a different method in the script run or as complex as to dictate what to search for in the data. For more information on how to apply configurations see the Integrated Post Analysis documentation or examples.`,
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={{ size: 12 }}>
            <ComponentBox>
              {configsError?.code === 'error' && !uploadModalVisible ? (
                <ICSErrorCard
                  code={configsError.code}
                  message={configsError.message}
                  fields={configsError.fields}
                />
              ) : (
                <div className='d-flex flex-column h-100'>
                  <TanStackDatagridWithExport
                    toolbarContent={
                      <ICSActionButton
                        id='configUploadButton'
                        type='success'
                        height={35}
                        stylingMode='contained'
                        onClick={() => {
                          setUploadModalVisible(true)
                        }}
                        text='Add'
                      />
                    }
                    data={data}
                    columns={columns}
                    actions={actions}
                    getRowId={(config) => String(config.id)}
                    sort={sort}
                    onSortChange={setSort}
                    filters={filters}
                    onFilterChange={setFilters}
                    enablePagination
                    pagination={pagination}
                    onPaginationChange={setPagination}
                    columnVisibility={columnVisibility}
                    onColumnVisibilityChange={setColumnVisibility}
                  />
                </div>
              )}
              <ICSModal
                visible={uploadModalVisible}
                onHiding={hideUploadModal}
                title='Add a Configuration'
              >
                <ModalBody>
                <span>
                  {uploadModalVisible && (
                    <ConfigSave
                      hideModal={hideUploadModal}
                      scriptId={scriptId}
                    />
                  )}
                </span>
                </ModalBody>

              </ICSModal>

              <ICSModal
                visible={modalVisible}
                onHiding={hideModal}
                title='Configuration Viewer'
                defaultWidth='95vw'
                defaultHeight='95vh'
              >
                <ModalBody>
                <span>
                  {modalVisible && selectedConfigId && (
                    <ConfigReview
                      script={scriptId}
                      configId={selectedConfigId}
                    />
                  )}
                </span>
                </ModalBody>
              </ICSModal>
            </ComponentBox>
          </Col>
        </Row>
      </Container>
    </>
  )
}
export default Configs
